/**
 * Channel Modules Page
 *
 * Per-channel module configuration. Lists every available module and whether
 * it's enabled FOR THIS CHANNEL, with a toggle (enable/disable) and a link to
 * the module's channel-scoped settings.
 *
 * Data model:
 * - The module catalog comes from GET /api/modules (global list).
 * - Per-channel enabled-state is derived from the channel's `enabled_modules`
 *   (GET /api/channels/:id), so load is two fetches, not N.
 * - Toggling calls POST /api/modules/:name/enable|disable/:channelId.
 *
 * The global catalog/registry concerns (hot-reload, "active in N channels",
 * availability) live in the Admin → Module Registry view, not here.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import api, { ApiError, apiErrorMessage } from '@/api/client'
import { useToast } from '@/components/Toast'
import { hasSettings } from '@/modules'
import {
  Puzzle,
  Settings,
  MessageSquare,
  Layout as LayoutIcon,
  Loader2,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react'
import clsx from 'clsx'

/**
 * Module info from GET /api/modules.
 */
interface ModuleInfo {
  name: string
  display_name: string
  description: string | null
  version: string | null
  author: string | null
  has_chat_commands: boolean
  has_web_ui: boolean
  has_settings: boolean
  /** Short names an external agent may push (R20 chat 4); non-empty = push key on the settings page. */
  agent_properties?: string[]
}

/**
 * Channel shape (subset of GET /api/channels/:id).
 */
interface Channel {
  id: number
  twitch_channel_name: string
  enabled_modules: string[]
}

/**
 * Small toggle switch.
 */
function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  onChange: () => void
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={clsx(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0',
        checked ? 'bg-synth-mint' : 'bg-neutral-600',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={clsx(
          'inline-block h-4 w-4 transform rounded-full bg-surface-app transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1'
        )}
      />
    </button>
  )
}

/**
 * Feature chip (chat commands / web ui).
 */
function FeatureChip({
  icon: Icon,
  label,
  on,
}: {
  icon: React.ElementType
  label: string
  on: boolean
}) {
  if (!on) return null
  return (
    <span className="badge badge-neutral" title={label}>
      <Icon size={12} />
      <span>{label}</span>
    </span>
  )
}

/**
 * One module row.
 */
function ModuleRow({
  module,
  channelId,
  enabled,
  pending,
  onToggle,
}: {
  module: ModuleInfo
  channelId: number
  enabled: boolean
  pending: boolean
  onToggle: () => void
}) {
  // Settings are reachable only if the module is enabled here AND it declares
  // a frontend settings page (registered in @/modules) — or accepts agent
  // pushes, whose key is managed on that page (R20 chat 4).
  const settingsAvailable =
    enabled && (hasSettings(module.name) || (module.agent_properties?.length ?? 0) > 0)

  return (
    <div className="card card-pad-sm flex items-center gap-4">
      <div className="w-10 h-10 rounded bg-synth-magenta/15 flex items-center justify-center shrink-0">
        <Puzzle className="text-synth-magenta" size={20} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-text-primary truncate">
            {module.display_name}
          </h3>
          {module.version && (
            <span className="text-xs text-text-muted">v{module.version}</span>
          )}
        </div>
        {module.description && (
          <p className="text-sm text-text-secondary truncate">
            {module.description}
          </p>
        )}
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          <FeatureChip
            icon={MessageSquare}
            label="Chat commands"
            on={module.has_chat_commands}
          />
          <FeatureChip icon={LayoutIcon} label="Web UI" on={module.has_web_ui} />
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {settingsAvailable && (
          <Link
            to={`/channels/${channelId}/modules/${module.name}`}
            className="btn-ghost btn-sm"
            title="Configure for this channel"
          >
            <Settings size={16} />
          </Link>
        )}
        {pending ? (
          <Loader2 className="animate-spin text-text-muted" size={20} />
        ) : (
          <Toggle checked={enabled} onChange={onToggle} />
        )}
      </div>
    </div>
  )
}

/**
 * Channel Modules page.
 */
export default function Modules() {
  const { channelId: channelIdParam } = useParams<{ channelId: string }>()
  const channelId = channelIdParam ? parseInt(channelIdParam, 10) : NaN

  const [channel, setChannel] = useState<Channel | null>(null)
  const [modules, setModules] = useState<ModuleInfo[]>([])
  const [enabled, setEnabled] = useState<Set<string>>(new Set())
  const [pending, setPending] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { success, error: errorToast } = useToast()

  // Load catalog + channel together.
  useEffect(() => {
    if (Number.isNaN(channelId)) {
      setError('Invalid channel')
      setIsLoading(false)
      return
    }

    let mounted = true
    Promise.all([
      api.get<ModuleInfo[]>('/api/modules'),
      api.get<Channel>(`/api/channels/${channelId}`),
    ])
      .then(([mods, ch]) => {
        if (!mounted) return
        setModules(mods)
        setChannel(ch)
        setEnabled(new Set(ch.enabled_modules))
        setError(null)
      })
      .catch((err) => {
        if (!mounted) return
        const msg =
          err instanceof ApiError && err.status === 404
            ? 'Channel not found'
            : 'Failed to load modules'
        setError(msg)
      })
      .finally(() => {
        if (mounted) setIsLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [channelId])

  // Toggle a module on/off for this channel.
  const toggle = useCallback(
    async (name: string) => {
      const currentlyEnabled = enabled.has(name)
      const verb = currentlyEnabled ? 'disable' : 'enable'
      const label = modules.find((m) => m.name === name)?.display_name || name

      setPending((prev) => new Set(prev).add(name))
      try {
        await api.post(`/api/modules/${name}/${verb}/${channelId}`)
        setEnabled((prev) => {
          const next = new Set(prev)
          currentlyEnabled ? next.delete(name) : next.add(name)
          return next
        })
        success(
          currentlyEnabled
            ? `${label} disabled for this channel.`
            : `${label} enabled for this channel.`,
        )
      } catch (err) {
        // The row is deliberately NOT flipped: the switch keeps showing what
        // the server actually has. Before R15 this catch was empty, so an
        // enable refused by the registry (403) looked exactly like one that
        // worked. The API answers with a written `detail` for every refusal
        // it makes on purpose -- the availability tier, the global kill
        // switch, a channel the caller cannot reach -- so that sentence is
        // what gets shown, and the generic line is only a fallback.
        errorToast(
          apiErrorMessage(
            err,
            `Could not ${verb} ${label}. Nothing was changed.`,
          ),
        )
      } finally {
        setPending((prev) => {
          const next = new Set(prev)
          next.delete(name)
          return next
        })
      }
    },
    [enabled, channelId, modules, success, errorToast]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-synth-magenta" size={32} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Link
          to="/channels"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft size={18} />
          Back to channels
        </Link>
        <div className="card flex items-center gap-3">
          <AlertCircle className="text-state-danger" size={20} />
          <span className="text-state-danger">{error}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={`/channels/${channelId}`}
          className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary mb-2"
        >
          <ArrowLeft size={16} />
          {channel?.twitch_channel_name}
        </Link>
        <h2 className="text-2xl font-bold text-text-primary">Modules</h2>
        <p className="text-text-secondary">
          Enable modules for this channel and configure their settings.
        </p>
      </div>

      {/* Module list */}
      {modules.length > 0 ? (
        <div className="space-y-3">
          {modules.map((module) => (
            <ModuleRow
              key={module.name}
              module={module}
              channelId={channelId}
              enabled={enabled.has(module.name)}
              pending={pending.has(module.name)}
              onToggle={() => toggle(module.name)}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Puzzle className="mx-auto text-text-muted mb-4" size={48} />
          <h3 className="text-lg font-medium text-text-primary mb-2">
            No modules available
          </h3>
          <p className="text-text-secondary">
            Modules will appear here once they are loaded.
          </p>
        </div>
      )}
    </div>
  )
}
